import { ROLES } from 'config/roles.config'
/**
 * TimeTrackerWidget
 *
 * A small floating widget that displays current time tracking stats.
 * Useful for testing and debugging the time tracker system.
 * When you're ready to roll it out to everyone, just remove:

The WIDGET_ALLOWED_USERS constant (line 40)
The user check (lines 92-94)
The useActiveUser import and userId variable can stay since they're lightweight, or remove them too for a clean removal
 */

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BarChartIcon from '@mui/icons-material/BarChart'
import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PauseIcon from '@mui/icons-material/Pause'
import PeopleIcon from '@mui/icons-material/People'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StorageIcon from '@mui/icons-material/Storage'
import TimerIcon from '@mui/icons-material/Timer'
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useMemo, useState } from 'react'

import { getTimeRangePreset, useActivitySummary, useRecentActivity } from 'api/aws/analytics'
import { useAreaBreakdown, useLivePageDuration, useLiveSessionDuration, useTimeTracker } from 'context/TimeTracker'
import { useNavigate } from 'react-router-dom'

// TODO: Remove this after testing is complete - only show widget to specific users
// const WIDGET_ALLOWED_USERS = ['U062JDP8R5X'] // Colin's Slack ID

interface TimeTrackerWidgetProps {
  /** Position of the widget */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  /** Whether to start expanded */
  defaultExpanded?: boolean
}
const TRACKER_ADMINS = [...ROLES.ADMIN_IDS]
/**
 * Get position styles based on position prop
 */
const getPositionStyles = (position: TimeTrackerWidgetProps['position']) => {
  const base = { position: 'fixed' as const, zIndex: 9999 }

  switch (position) {
    case 'bottom-left':
      return { ...base, bottom: 16, left: 16 }
    case 'top-right':
      return { ...base, top: 16, right: 16 }
    case 'top-left':
      return { ...base, top: 16, left: 16 }
    case 'bottom-right':
    default:
      return { ...base, bottom: 16, right: 16 }
  }
}

const TimeTrackerWidget: React.FC<TimeTrackerWidgetProps> = ({
  position = 'bottom-right',
  defaultExpanded = false,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [activeTab, setActiveTab] = useState(0) // 0 = Session, 1 = Saved Data
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')

  const isTrackerAdmin = useMemo(() => {
    if (!currentUser) return false
    return TRACKER_ADMINS?.includes(currentUser?.user_uuid)
  }, [currentUser])
  // Get active user for access check
  /*
  const { activeUser } = useActiveUser()
  const userId = activeUser?.user?.id
  */

  // Get tracking data
  const { isTracking, startTracking, stopTracking, visits, isWidgetVisible, hideWidget } = useTimeTracker()
  const { formattedDuration: pageDuration, currentVisit } = useLivePageDuration()
  const { formattedDuration: sessionDuration } = useLiveSessionDuration()
  const { sortedBreakdown } = useAreaBreakdown()

  // Memoize the time range to prevent re-fetching on every render
  const todayRange = useMemo(() => getTimeRangePreset('today'), [])

  // Get saved analytics data (only fetch when on the saved data tab)
  const { data: summaryData, isLoading: summaryLoading } = useActivitySummary({
    startTime: todayRange.startTime,
    endTime: todayRange.endTime,
    enabled: activeTab === 1,
  })
  const { data: recentData, isLoading: recentLoading } = useRecentActivity(10, activeTab === 1)

  // TODO: Remove this check after testing is complete
  // Only show widget to allowed users (tracking still happens for everyone)
  // if (!userId || !WIDGET_ALLOWED_USERS.includes(userId)) return null

  // Use context visibility
  if (!isWidgetVisible) return null

  const positionStyles = getPositionStyles(position)

  return (
    <Paper
      elevation={8}
      sx={{
        ...positionStyles,
        minWidth: 220,
        maxWidth: 320,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isTrackerAdmin ? (
            <IconButton onClick={() => navigate('/activity-report')}>
              <AccessTimeIcon
                sx={{
                  fontSize: '1.1rem',
                  color: isTracking ? theme.palette.primary.main : theme.palette.text.disabled,
                }}
              />
            </IconButton>
          ) : (
            <AccessTimeIcon
              sx={{
                fontSize: '1.1rem',
                color: isTracking ? theme.palette.primary.main : theme.palette.text.disabled,
              }}
            />
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Time Tracker
          </Typography>
          {isTracking && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: theme.palette.success.main,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={isTracking ? 'Pause tracking' : 'Resume tracking'}>
            <IconButton size="small" onClick={() => (isTracking ? stopTracking() : startTracking())} sx={{ p: 0.5 }}>
              {isTracking ? <PauseIcon sx={{ fontSize: '1rem' }} /> : <PlayArrowIcon sx={{ fontSize: '1rem' }} />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: '1.1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />}
          </IconButton>
          <Tooltip title="Hide widget">
            <IconButton size="small" onClick={hideWidget} sx={{ p: 0.5 }}>
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Current Page Info */}
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <LocationOnIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main, mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
              Current Location
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }} noWrap>
              {currentVisit?.area || 'Unknown'}
              {currentVisit?.entityName && (
                <Typography component="span" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>
                  / {currentVisit.entityName}
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimerIcon sx={{ fontSize: '0.9rem', color: theme.palette.text.secondary }} />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Page:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {pageDuration}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: '0.9rem', color: theme.palette.text.secondary }} />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Session:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {sessionDuration}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                minHeight: 32,
                py: 0.5,
                fontSize: '0.7rem',
                textTransform: 'none',
              },
            }}
          >
            <Tab
              icon={<TimerIcon sx={{ fontSize: '0.9rem' }} />}
              iconPosition="start"
              label="Session"
              sx={{ gap: 0.5 }}
            />
            <Tab
              icon={<StorageIcon sx={{ fontSize: '0.9rem' }} />}
              iconPosition="start"
              label="Saved"
              sx={{ gap: 0.5 }}
            />
          </Tabs>

          {/* Session Tab Content */}
          {activeTab === 0 && (
            <Box sx={{ px: 1.5, py: 1 }}>
              {/* Area Breakdown */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <BarChartIcon sx={{ fontSize: '0.9rem', color: theme.palette.text.secondary }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Time by Area
                </Typography>
              </Box>

              {sortedBreakdown.length === 0 ? (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  No areas visited yet
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {sortedBreakdown.slice(0, 5).map(({ area, formatted }) => (
                    <Box
                      key={area}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" sx={{ flex: 1 }}>
                        {area}
                      </Typography>
                      <Chip
                        label={formatted}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Visit Count */}
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1,
                  borderTop: `1px dashed ${alpha(theme.palette.divider, 0.2)}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Pages visited this session:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {visits.length + (currentVisit ? 1 : 0)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Saved Data Tab Content */}
          {activeTab === 1 && (
            <Box sx={{ px: 1.5, py: 1 }}>
              {summaryLoading || recentLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : summaryData ? (
                <>
                  {/* Today's Summary */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Today's Activity
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Total Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {summaryData.summary.totalDurationFormatted}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Visits
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {summaryData.summary.totalVisits}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Users
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {summaryData.summary.uniqueUsers}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Top Areas */}
                  {summaryData.areaBreakdown.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <BarChartIcon sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Top Areas (Today)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {summaryData.areaBreakdown.slice(0, 3).map(({ area, durationFormatted, visits }) => (
                          <Box
                            key={area}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="caption" sx={{ flex: 1 }}>
                              {area}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 1 }}>
                              {visits} visits
                            </Typography>
                            <Chip
                              label={durationFormatted}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                color: theme.palette.success.main,
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Recent Activity */}
                  {recentData?.recentActivity && recentData.recentActivity.length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <PeopleIcon sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Recent Activity
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {recentData.recentActivity.slice(0, 5).map((activity, idx) => (
                          <Box
                            key={`${activity.userId}-${activity.timestamp}-${idx}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 0.25,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{ fontWeight: 500 }} noWrap>
                                {activity.userName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary, display: 'block' }}
                                noWrap
                              >
                                {activity.area}
                                {activity.entityName && ` / ${activity.entityName}`}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.secondary, ml: 1, flexShrink: 0 }}
                            >
                              {activity.timeAgo}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  No saved activity data yet. Activity is saved when you navigate away from a page.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default TimeTrackerWidget
